import { useContext, useCallback } from 'react';
import set from 'lodash.set';
import cloneDeep from 'lodash.clonedeep';

import dt from '~/testing';

import { StaticFieldDescriptorsMap } from '../StaticField/constants';
import type { StaticField } from '../StaticField/types';
import { useSectionsNavigation } from '../../context/hooks/useSectionsNavigation';
import { ContestManageContext } from '../../context/ContestManageContext';
import { getFieldQueryByField } from '../../context/utils/getFieldQueryByField';
import { useFieldValue } from '../../hooks/useFieldValue';
import type { FieldValue } from '../../types/FieldValue';

export function StaticFieldComponent({
  field,
  sectionKey,
  FieldSwitch,
  path,
  isVisible,
  propagateFieldLabelToParent,
}: {
  field: StaticField;
  sectionKey: string;
  FieldSwitch: (props: { fieldKey: string; sectionKey: string }) => JSX.Element;
  path: string[];
  isVisible?: boolean;
  propagateFieldLabelToParent?: (path: string[], label: string | string[]) => void;
}) {
  const {
    values: allValues,
    errors: allErrors,
    store,
    setValues,
    setTouched,
  } = useContext(ContestManageContext);

  // Create stable callbacks for field lookup (used by container fields like ModalFieldGroup)
  const getFieldByKey = useCallback((fieldKey: string) => store.fields[fieldKey], [store.fields]);

  const getFieldQueryData = useCallback(
    (fieldQuery: Parameters<typeof getFieldQueryByField>[1]) =>
      getFieldQueryByField(store, fieldQuery),
    [store]
  );

  // Create a callback to set any field value by path (used by ModalFieldGroup to clear child fields)
  // clearTouched: if true, clears the touched state (used when resetting to allow defaultValue to apply)
  const setFieldValue = useCallback(
    (
      fieldPath: string[],
      newValue: unknown,
      options?: { skipTouched?: boolean; clearTouched?: boolean }
    ) => {
      setValues((prev) => set(cloneDeep(prev), [sectionKey, ...fieldPath], newValue));
      if (options?.clearTouched) {
        setTouched((prev) => set(cloneDeep(prev), [sectionKey, ...fieldPath], false));
      } else if (!options?.skipTouched) {
        setTouched((prev) => set(prev, [sectionKey, ...fieldPath], true));
      }
    },
    [sectionKey, setValues, setTouched]
  );
  const { onGoToSection, onNextSectionClick } = useSectionsNavigation();
  const { value, onValueChange, errors, isTouched } = useFieldValue(
    sectionKey,
    path,
    'defaultValue' in field ? (field.defaultValue as FieldValue) : undefined,
    isVisible
  );
  const isHiddenField = field.type === 'HiddenField';

  const FieldDescriptor = StaticFieldDescriptorsMap[field.type];

  if (!FieldDescriptor) {
    return <div>Unknown field type: {field.type}</div>;
  }

  if (!FieldDescriptor.renderIfHidden && !isVisible) {
    return null;
  }

  if (!FieldDescriptor.FieldComponent) {
    return null;
  }

  if (isHiddenField && !FieldDescriptor.renderIfHidden) return null;

  if (FieldDescriptor.rendersNull) {
    return (
      <FieldDescriptor.FieldComponent
        field={field}
        sectionKey={sectionKey}
        value={value}
        onValueChange={onValueChange}
        errors={errors}
        isTouched={isTouched}
        allValues={allValues}
        allErrors={allErrors}
        FieldSwitch={FieldSwitch}
        path={path}
        onGoToSection={onGoToSection}
        onNextSectionClick={onNextSectionClick}
        isVisible={isVisible}
        propagateFieldLabelToParent={propagateFieldLabelToParent}
        getFieldByKey={getFieldByKey}
        getFieldQueryData={getFieldQueryData}
        setFieldValue={setFieldValue}
      />
    );
  }

  return (
    <div data-test-id={dt.contest.create.staticFieldComponent} id={path.join('-')}>
      <FieldDescriptor.FieldComponent
        field={field}
        sectionKey={sectionKey}
        value={value}
        onValueChange={onValueChange}
        errors={errors}
        isTouched={isTouched}
        allValues={allValues}
        allErrors={allErrors}
        FieldSwitch={FieldSwitch}
        path={path}
        onGoToSection={onGoToSection}
        onNextSectionClick={onNextSectionClick}
        isVisible={isVisible}
        propagateFieldLabelToParent={propagateFieldLabelToParent}
        getFieldByKey={getFieldByKey}
        getFieldQueryData={getFieldQueryData}
        setFieldValue={setFieldValue}
      />
    </div>
  );
}
